import type { BookingState } from './bookingState';

/**
 * El MOMENTO de la reserva: qué está decidiendo el asesor ahora.
 *
 * El modal muestra UNO a la vez — nunca el formulario y el resultado juntos —
 * y lo decide el estado del booking, que es el registro autoritativo:
 *
 * - `review`   (Revisar): todavía no hay nada del otro lado. Es el único
 *   momento con formulario.
 * - `reserved` (Reservado): el proveedor tiene la reserva y el reloj corre.
 * - `confirmed` (Confirmado): documentos y registro comercial.
 * - `closed`   (Cerrado): cancelada o fallida. No es un error de la pantalla:
 *   es el final del ciclo y se lee como registro.
 *
 * No hay vuelta atrás a `review` una vez que existe la reserva: sería mentir.
 */
export type BookingMoment = 'review' | 'reserved' | 'confirmed' | 'closed';

/**
 * Las secciones que puede dibujar el modal. Cada momento declara las suyas y
 * el componente dibuja EXACTAMENTE esa lista, en ese orden: una sola
 * expresión gobierna qué se muestra, así no puede divergir de lo que se
 * afirma acá.
 */
export type BookingMomentSection =
  /**
   * El reprecio del proveedor al reservar: va PRIMERO, antes que nada de lo
   * que hay que completar, porque cambia la decisión entera.
   */
  | 'reprice'
  /** Datos del viaje: resumen del pedido/reserva + mapa del recorrido. */
  | 'trip'
  /** Horarios y vuelos por tramo (transfers), con re-cotización al cambiarlos. */
  | 'schedule'
  /** Lo que la oferta deja elegir: plazas, asientos, tarifa, hold del proveedor. */
  | 'offerOptions'
  /** Titular y acompañantes, dirigidos por lo que declara la oferta. */
  | 'passengers'
  /**
   * Los adicionales opcionales que publica la OFERTA: se eligen (cantidad y
   * tramo) y se reservan EN el mismo hold (#2383).
   */
  | 'optionalItems'
  /** La letra chica de la oferta y la ocupación cotizada. */
  | 'conditions'
  /** Estado de la reserva y sus avisos de recuperación. */
  | 'status'
  /** Lo que quedó reservado según el proveedor. */
  | 'reserved'
  /** Las preguntas que el operador exige para emitir. */
  | 'questions'
  /**
   * Lo que falta para poder confirmar: los datos que el proveedor valida
   * recién al confirmar y el bloqueo durable que él mismo publicó.
   */
  | 'confirmPending'
  /** Los documentos del viaje. */
  | 'documents'
  /** Los servicios adicionales del proveedor, sobre la reserva ya creada. */
  | 'extras'
  /** El registro comercial congelado (total, neto, markup). */
  | 'record';

export interface ResolveBookingMomentInput {
  booking: { state: BookingState } | null | undefined;
  /**
   * La oferta elegida todavía se puede holdear: sigue siendo de la búsqueda
   * comiteada, no venció y ningún rechazo la invalidó. En falso el momento de
   * Revisar se queda SOLO con los datos del viaje — no hay formulario que
   * completar sobre una oferta que ya no se puede reservar, y la salida es
   * volver a buscar (el aviso del cuerpo lo dice).
   */
  offerHoldable: boolean;
}

export interface BookingMomentView {
  moment: BookingMoment;
  /**
   * Ya existe una reserva del otro lado. Gobierna el formulario: con reserva
   * creada no se dibuja NUNCA, ni siquiera en `review` — el único camino a un
   * segundo hold sobre la misma reserva sería duplicarla en el proveedor.
   */
  hasBooking: boolean;
  sections: BookingMomentSection[];
}

/** El momento de Revisar sin reserva: todo lo que se decide antes de holdear. */
const PRE_HOLD_SECTIONS: BookingMomentSection[] = [
  'trip',
  'schedule',
  'offerOptions',
  'passengers',
  // Lo que el proveedor ofrece aparte: se lee después de lo que hay que
  // completar, pero SÍ decide — lo elegido se reserva en el mismo hold y
  // mueve el total que devuelve el hub (#2383).
  'optionalItems',
  'conditions',
];

/**
 * Del estado del contrato al momento.
 *
 * ⚠️ `draft` es post-hold, no pre-hold: es la fila write-ahead que el backend
 * crea ANTES de llamar al proveedor y que devuelve tal cual cuando el
 * resultado es ambiguo (`unknownProviderState`). Puede haber una reserva viva
 * del otro lado, así que mostrar de nuevo el formulario sería ofrecer un
 * segundo hold sobre una reserva que quizá ya existe.
 *
 * `quoted` sí es Revisar: el contrato lo define como cotizado sin reservar. Lo
 * absorbe el momento 1 con el precio ya cotizado, sin formulario, porque no
 * hay canal para holdear una reserva existente (hoy ningún conector lo
 * produce; la UI igual soporta todo estado que el backend pueda producir).
 */
export function bookingMomentOf(state: BookingState | undefined): BookingMoment {
  switch (state) {
  case 'draft':
  case 'held':
    return 'reserved';
  case 'confirmed':
    return 'confirmed';
  case 'cancelled':
  case 'failed':
    return 'closed';
  default:
    return 'review';
  }
}

export function resolveBookingMoment({
  booking,
  offerHoldable,
}: ResolveBookingMomentInput): BookingMomentView {
  if (!booking) {
    return {
      moment: 'review',
      hasBooking: false,
      sections: offerHoldable ? [...PRE_HOLD_SECTIONS] : ['trip'],
    };
  }

  const moment = bookingMomentOf(booking.state);
  const sections: BookingMomentSection[] = ['trip', 'status', 'reserved'];
  // El reprecio se lee con la reserva recién creada y el reloj corriendo: es
  // el momento en que todavía se puede no confirmar, así que va PRIMERO — no
  // debajo de lo que falta completar. Después de confirmar el número ya se
  // aceptó, y el registro comercial lo dice entero.
  if (moment === 'reserved') sections.unshift('reprice');
  // Las preguntas del operador se contestan con la reserva en hold: después de
  // confirmar ya no hay nada que responder.
  if (moment === 'reserved') sections.push('questions');
  // Lo que falta para confirmar va DESPUÉS de las preguntas y antes del
  // registro: es lo último que el asesor lee antes de decidir, pegado al CTA
  // que bloquea. Confirmada o cerrada la reserva ya no hay nada que completar.
  if (moment === 'reserved') sections.push('confirmPending');
  // Documentos: solo los emite una reserva confirmada.
  if (moment === 'confirmed') sections.push('documents');
  // Los extras se compran sobre la reserva YA creada (no tienen canal en el
  // hold), así que viven en los dos momentos que la tienen. Van últimos de lo
  // accionable, pegados al registro comercial: es la plata que se le suma a lo
  // reservado, y lo que bloquea el confirm se lee antes.
  if (moment === 'reserved' || moment === 'confirmed') sections.push('extras');
  // El registro comercial se lee con la reserva creada, en cualquiera de sus
  // momentos — incluido el cotizado, que es lo único que ese momento tiene.
  // También en Reservado: el neto y el markup CONGELADOS son parte de lo que
  // el asesor mira antes de confirmar, sobre todo cuando el proveedor
  // repreció, y salen de la fila (el frontend no recalcula nada monetario).
  sections.push('record');

  return { moment, hasBooking: true, sections };
}
